// ABOUTME: An upgrade reads as unpaid until its payment webhook lands, and coming back from the payment page tells
// ABOUTME: nothing of the outcome, so for two minutes after it the upgrade is checked on, not offered for replacement.

import { useEffect, useReducer } from "react"
import { useApolloClient } from "@apollo/client/react"

// Everything a plan change moves: the subscriptions, the paid-through dates (the user's permissions, loaded for
// `me` by useUser with userOld — getMe carries none), the wallet and the payment history.
// Refetch by operation name so Apollo invalidates every observed variable
// set (e.g. subscriptionList may be observed with different filters by
// sibling components in the future).
export const PLAN_CHANGE_QUERIES = ["subscriptionList", "userOld", "getMe", "userWallets", "paymentListForBuyer"]

const POLL_MS = 3000
// A webhook usually lands within seconds. After two minutes the upgrade reads as unpaid again: the user who came
// back may just as well have abandoned the checkout (YooKassa sends everyone back to the success URL).
const CHECK_MS = 2 * 60_000

// The upgrade checkouts this tab has sent to the payment service. Kept per tab, not in the history entry: the
// settings switch tabs by navigating to a fresh entry, and a check must hold until it runs out.
const UPGRADE_CHECKOUTS_KEY = "upgrade_checkouts"

interface UpgradeCheckout {
  upgradeId: string
  paymentURL: string
  /** When the user came back from the payment page while its payment was unsettled; null when not checking. */
  returnedAt: number | null
}

const isUpgradeCheckout = (value: unknown): value is UpgradeCheckout =>
  typeof value === "object" &&
  value !== null &&
  "upgradeId" in value &&
  typeof value.upgradeId === "string" &&
  "paymentURL" in value &&
  typeof value.paymentURL === "string" &&
  "returnedAt" in value &&
  (value.returnedAt === null || typeof value.returnedAt === "number")

const readCheckouts = (): UpgradeCheckout[] => {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(UPGRADE_CHECKOUTS_KEY) ?? "[]")
    return Array.isArray(parsed) ? parsed.filter(isUpgradeCheckout) : []
  } catch {
    return []
  }
}

const writeCheckouts = (checkouts: readonly UpgradeCheckout[]): void => {
  try {
    sessionStorage.setItem(UPGRADE_CHECKOUTS_KEY, JSON.stringify(checkouts))
  } catch {
    // sessionStorage unavailable: the settings show the upgrade as unpaid, with nothing held back.
  }
}

const setReturnedAt = (matches: (checkout: UpgradeCheckout) => boolean, returnedAt: number | null): void =>
  writeCheckouts(readCheckouts().map((checkout) => (matches(checkout) ? { ...checkout, returnedAt } : checkout)))

/** The user leaves this tab for `paymentURL` to pay for the upgrade `upgradeId`. */
export const rememberUpgradeCheckout = (upgradeId: string, paymentURL: string): void =>
  writeCheckouts([...readCheckouts(), { upgradeId, paymentURL, returnedAt: null }])

/**
 * The user is back from the payment page at `paymentURL`, its payment still unsettled: the upgrade this tab sent
 * there — and no other, say one that replaced it — is checked on for a while.
 */
export const rememberReturnedCheckout = (paymentURL: string): void =>
  setReturnedAt((checkout) => checkout.paymentURL === paymentURL, Date.now())

const returnedAtOf = (upgradeId: string): number | null =>
  readCheckouts().find((checkout) => checkout.upgradeId === upgradeId && checkout.returnedAt !== null)?.returnedAt ??
  null

/**
 * True while the user has just come back from paying for `pendingUpgradeId` and it still awaits its payment — the
 * card neither offers to replace it (a payment in flight would go to the wallet) nor calls it unpaid. The data a
 * plan change moves is re-read meanwhile; two minutes after the return the upgrade gets its actions back.
 */
export const useUpgradePaymentConfirmation = (pendingUpgradeId: string | null): boolean => {
  const client = useApolloClient()
  const [, rerender] = useReducer((renders: number) => renders + 1, 0)
  const returnedAt = pendingUpgradeId === null ? null : returnedAtOf(pendingUpgradeId)

  useEffect(() => {
    if (pendingUpgradeId === null || returnedAt === null) return
    // A failed refetch is caught by Apollo itself (ApolloClient.js:466-468) and the next poll tries again.
    const poll = setInterval(() => void client.refetchQueries({ include: PLAN_CHANGE_QUERIES }), POLL_MS)
    // Counted from the return, not from this mount: reopening the settings does not start the check over.
    const end = setTimeout(
      () => {
        setReturnedAt((checkout) => checkout.upgradeId === pendingUpgradeId, null)
        rerender()
      },
      Math.max(0, returnedAt + CHECK_MS - Date.now()),
    )
    return () => {
      clearInterval(poll)
      clearTimeout(end)
    }
  }, [pendingUpgradeId, returnedAt, client])

  return returnedAt !== null
}
